import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {useTranslation} from 'common/i18n/hooks';
import {FeatureGate} from 'common/report/constants';
import {buildCreateEntityModalAttrs, getEntityType, getEntityTypeName} from 'common/report/util/saved-entities';
import {Entity, EntityType, SharableEntityType} from 'common/report/util/saved-entities/types';
import {findKeyByValue} from 'common/util/typescript';
import {createEntityModalPromise} from 'common/widgets/create-entity-modal';
import {promptSharing} from 'common/widgets/sharing';

/**
 * Attempts to execute a provided asynchronous function and returns the first value from the resulting record.
 * If the function throws an error, a generic error toast is displayed and null is returned.
 */
export async function attemptRequest<Type>(fn: () => Promise<Record<string, Type>>): Promise<Type | null> {
  try {
    const results = await fn();
    return Object.values(results)[0];
  } catch {
    addToast({
      message: ({t}) => t(`common.generic-error`),
      type: ToastType.ERROR,
      timeout: undefined,
    });
    return null;
  }
}

/**
 * Utils for saving and sharing a given @type{Entity}
 */
export function useSaveAndShareFlow<Type, RequestType>({
  createFn,
  entityType,
  existingNamesFn,
  sharableEntityType,
}: {
  createFn: (entity: RequestType) => Promise<Record<string, Type>>;
  entityType: EntityType;
  existingNamesFn: () => string[];
  sharableEntityType?: SharableEntityType;
}) {
  const i18n = useTranslation();
  const currentProject = useCurrentProject();
  const projectId = Number.parseInt(currentProject.id);

  /** Initiates a Save & Share workflow for an unsaved behavior  */
  const createNewEntity = async (
    entity: RequestType,
    {isDuplicating = false}: {isDuplicating?: boolean} = {},
  ): Promise<Type | null> => {
    const createModalResult = await createEntityModalPromise(
      buildCreateEntityModalAttrs({
        projectId,
        entityType,
        modalOpen: true,
        isEditing: false,
        isDuplicating,
        entity: entity as Entity,
        showShareManagement: currentProject.get()?.value?.getFeatureGate(FeatureGate.Sharing) ?? false,
        forceShareFlow: true,
        i18n,
      }),
      {
        existingEntityNames: existingNamesFn(),
      },
    );

    if (!createModalResult.didSave) {
      return null;
    }

    const result = await attemptRequest<Type>(() =>
      createFn({
        ...entity,
        name: createModalResult.name,
        description: createModalResult.description,
      }),
    );

    if (result && createModalResult.didShare) {
      const shareableType: SharableEntityType | undefined =
        sharableEntityType ?? SharableEntityType[findKeyByValue(SharableEntityType, entityType)];
      if (!shareableType) {
        throw new Error(`Unsupported entity type`);
      }
      const entityTypeName: string = i18n.print(getEntityTypeName(getEntityType(shareableType), {titleCased: true}));

      promptSharing({
        type: shareableType,
        id: (result as unknown as Entity).id,
        'project-id': projectId,
        'success-message': i18n.t(`first-class-metrics-info.successful-save`, {entityTypeName}),
      });
    }

    return result;
  };

  return createNewEntity;
}
